// Auth Store - handles user authentication state with Devise
import { defineStore } from 'pinia'
import apiClient, { railsClient } from '../plugins/axios'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    isAuthenticated: false,
    loading: false,
    error: null,
  }),

  getters: {
    currentUser: (state) => state.user,
    isLoggedIn: (state) => state.isAuthenticated,
    isLoading: (state) => state.loading,
    hasError: (state) => !!state.error,
    userName: (state) => state.user ? state.user.full_name || state.user.email : null,
  },

  actions: {
    setLoading(loading) {
      this.loading = loading
    },

    setError(error) {
      this.error = error
    },

    clearError() {
      this.error = null
    },

    setUser(user) {
      this.user = user
      this.isAuthenticated = !!user
    },

    clearUser() {
      this.user = null
      this.isAuthenticated = false
    },

    async checkAuth() {
      this.setLoading(true)
      this.clearError()

      try {
        const response = await apiClient.get('/current_user')
        if (response.data.authenticated) {
          this.setUser(response.data.user)
          return true
        } else {
          this.clearUser()
          return false
        }
      } catch (error) {
        console.warn('Auth check failed:', error.message)
        this.clearUser()
        return false
      } finally {
        this.setLoading(false)
      }
    },

    async login(credentials) {
      this.setLoading(true)
      this.clearError()

      try {
        const response = await railsClient.post('/users/sign_in', {
          user: {
            email: credentials.email,
            password: credentials.password
          }
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        })

        this.setUser(response.data.user)
        return { success: true, user: response.data.user }
      } catch (error) {
        const message = error.response?.data?.errors?.join(', ') || error.response?.data?.error || 'Connexion echouee'
        this.setError(message)
        return { success: false, error: message }
      } finally {
        this.setLoading(false)
      }
    },

    async register(userData) {
      this.setLoading(true)
      this.clearError()

      try {
        const response = await railsClient.post('/users', {
          user: {
            email: userData.email,
            password: userData.password,
            password_confirmation: userData.password_confirmation,
            first_name: userData.first_name,
            last_name: userData.last_name
          }
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        })

        this.setUser(response.data.user)
        return { success: true, user: response.data.user }
      } catch (error) {
        const message = error.response?.data?.errors?.join(', ') || 'Inscription echouee'
        this.setError(message)
        return { success: false, error: message }
      } finally {
        this.setLoading(false)
      }
    },

    async logout() {
      this.setLoading(true)

      try {
        await railsClient.delete('/users/sign_out', {
          headers: {
            'Accept': 'application/json'
          }
        })
      } catch (error) {
        console.warn('Logout API call failed:', error.message)
      } finally {
        this.clearUser()
        this.setLoading(false)
      }
    }
  }
})
